import styled from 'styled-components';
import React, { useEffect, useState } from 'react';

import { Virtuoso } from 'react-virtuoso';
import { LayoutColors } from '@src/core/theme';
import { Order } from '@src/core/buyerApiClient';
import { device } from '@src/common/breakpoints';
import useTranslate from '@src/i18n/useTranslate';
import { buyerSelector } from '@src/features/auth';
import { useAppSelector, useAppDispatch } from '@src/core/hooks';
import { loadOrders, ordersSelector, isLoadingOrdersSelector } from '@src/features/order';

import OrderCard from './OrderCard';

type Props = {
  onOrderSelection: (order: Order) => void;
  onOrderCheckout: (order: Order) => void;
};

const OrderList = (props: Props) => {
  const { onOrderSelection, onOrderCheckout } = props;
  const dispatch = useAppDispatch();
  const [, logged] = useAppSelector(buyerSelector);
  const [page, setPage] = useState(1);
  const { orders, totalPages } = useAppSelector(ordersSelector);
  const isLoading = useAppSelector(isLoadingOrdersSelector);

  const hasMore = totalPages > page;

  const { i18n } = useTranslate();

  useEffect(() => {
    if (logged) {
      dispatch(loadOrders(page));
    }
  }, [logged]);

  const handleEndReached = () => {
    dispatch(loadOrders(page + 1));
    setPage(page + 1);
  };

  return (
    <Virtuoso
      data={orders!}
      className='virtuoso-container'
      endReached={hasMore ? handleEndReached : undefined}
      itemContent={(index, order) => (
        <ShoppingCardWrapper key={order.id}>
          <OrderCard
            onOrderCheckout={onOrderCheckout}
            onOrderSelection={onOrderSelection}
            order={order}
            showReservationsPreview={index === 0}
          />
        </ShoppingCardWrapper>
      )}
      components={{
        Footer: () =>
          isLoading ? (
            <LoadingContainer>
              <span>{i18n('base_loading')}</span>
            </LoadingContainer>
          ) : null,
      }}
    />
  );
};

const ShoppingCardWrapper = styled.div`
  margin-bottom: 16px !important;

  &:last-of-type {
    margin-bottom: 0;
  }

  ${device.smallTablet} {
    margin-right: 8px !important;
    padding: 0 4px 0 0;
  }
`;

const LoadingContainer = styled.div`
  padding-top: 16px;
  display: flex;
  justify-content: center;

  span {
    color: ${LayoutColors.blackMain};
  }
`;

export default OrderList;
